import { buildWaiter } from '@ember/test-waiters';

let waiter = buildWaiter('js-ast:find-used-variable-paths');

export async function findUsedVariablePaths(js: string): Promise<(string | number | bigint | boolean | RegExp)[][]> {
  let token = waiter.beginAsync();

  try {
    let acorn = await import('acorn');
    let result = acorn.parse(js, { ecmaVersion: 'latest', allowReturnOutsideFunction: true });

    let walk = await import('acorn-walk');

    let pathsMap: Record<string, (string | number | bigint | boolean | RegExp)[]> = {};
    walk.simple(result, {
      MemberExpression(node) {
        // We use the "nodeStartEndId" properties to uniquely identify a whole
        // nodes path.
        // walk.simple will call this function for every MemberExpression, even
        // if it's nested in another.
        // For example, trigger.data.customer.first_name is a MemberExpression,
        // but so is data.customer.first_name and customer.first_name, and there
        // is no option to disable walking through these nested properties.
        // recurseMemberExpression walks the MemberExpression tree to the end,
        // and returns a unique ID corresponding to the root section of the
        // path.
        // When sub paths are evaluated, we will only ever keep the longest path
        // for a particular member expression, to make sure what we finally keep
        // is the full path.
        let result = recurseMemberExpression(node as any);
        if (pathsMap[result.id] == null || pathsMap[result.id].length < result.keys.length) {
          pathsMap[result.id] = result.keys;
        }
      },
    });

    return Object.values(pathsMap);
  } catch {
    return [];
  } finally {
    waiter.endAsync(token);
  }
}

// Walks through a MemberExpression to get the whole access path.
// Anything like trigger.data.customer.first_name is a MemberExpression, but
// each part of the path is broken up into nested MemberExpressions, so that's
// why we need to traverse
function recurseMemberExpression(
  node: import('acorn').Node & import('estree').MemberExpression,
  parentKeysFromTop: (string | number | bigint | boolean | RegExp)[] = [],
) {
  // We construct a unique ID based on a node's "start" and "end" properties,
  // which tell us the character positions of where the node is located in the
  // code.
  let nodeStartEndId = `${node.start}_${node.end}`;
  if (node.property.type !== 'Identifier' && node.property.type !== 'Literal') {
    // This would be paths like trigger.data[myFunc(something)]. We'll ignore
    // those - we're only looking for simple path access.
    return {
      id: nodeStartEndId,
      keys: parentKeysFromTop.reverse(),
    };
  }

  // Identifier: trigger.data
  // Literal: trigger['data'] or trigger[0]

  if (node.property.type === 'Identifier') {
    parentKeysFromTop.push(node.property.name);
  } else if (node.property.type === 'Literal') {
    if (node.property.value != null) {
      parentKeysFromTop.push(node.property.value);
    }
  }

  if (node.object.type === 'Identifier') {
    parentKeysFromTop.push(node.object.name);
  } else if (node.object.type === 'MemberExpression') {
    // Nested object. For example: trigger.data.person.name, the top
    // MemberExpression would unpack into something like:
    // {
    //   type: 'MemberExpression',
    //   property: {
    //     type: 'Identifier',
    //     name: 'name'
    //   },
    //   object: {
    //     type: 'MemberExpression',
    //     property: {
    //       type: 'Identifier',
    //       name: 'person'
    //     },
    //     object: {
    //       type: 'Identifier',
    //       property: {
    //         type: 'Identifier',
    //         name: 'data'
    //       },
    //       object: {
    //         type: 'Identifier',
    //         name: 'trigger'
    //       }
    //     }
    //   }
    // }
    // So we need to recurse down and assemble the path
    let result = recurseMemberExpression(node.object as any, parentKeysFromTop);
    // We'll use the "nodeStartEndId" property for the last part of a
    // MemberExpression subpath, to uniquely identify a whole path. Because the
    // "object" tree is sort of reversed in the nesting, this will actually
    // correspond to the root of the path.
    nodeStartEndId = result.id;
    parentKeysFromTop = result.keys.reverse();
  }
  // ^ there may be other object types, but we'll ignore them.
  // This should cover the simple cases we're looking for

  return {
    id: nodeStartEndId,
    keys: parentKeysFromTop.reverse(),
  };
}
